import { nanoid } from "nanoid";

import { UACT_DENOM } from "@src/config/denom.config";
import type { EndpointType, ExposeType, PlacementType, SdlBuilderFormValuesType, ServiceType } from "@src/types";
import { HELLO_WORLD_IMAGE, HELLO_WORLD_PORT } from "@src/utils/templates";
import { SSH_VM_IMAGES } from "./vmImages";

export { SSH_VM_IMAGES };

export const protoTypes = [
  { id: 1, name: "http" },
  // { id: 2, name: "https" },
  { id: 3, name: "tcp" },
  { id: 4, name: "udp" }
];

/** The provider's own `next_timeout` for an SDL that leaves it out, unlike the one a new port is seeded with. */
export const SDL_DEFAULT_NEXT_TIMEOUT = 0;

export const defaultHttpOptions = {
  maxBodySize: 1048576,
  readTimeout: 60000,
  sendTimeout: 60000,
  nextCases: ["error", "timeout"],
  nextTries: 3,
  nextTimeout: 60000
};

/**
 * Builds a fresh placement with a generated id and the dcloud defaults.
 */
export const defaultPlacement = (overrides?: Partial<PlacementType>): PlacementType => ({
  id: nanoid(),
  name: "dcloud",
  signedBy: {
    anyOf: [],
    allOf: []
  },
  attributes: [],
  ...overrides
});

/** Builds a fresh IP endpoint with a generated id and the default name "endpoint-1"; callers pass a unique name via overrides. */
export const defaultEndpoint = (overrides?: Partial<EndpointType>): EndpointType => ({
  id: nanoid(),
  name: "endpoint-1",
  ...overrides
});

/**
 * Builds a fresh per-service pricing object with the default max price.
 */
export const defaultPricing = (): ServiceType["pricing"] => ({
  amount: 100000,
  denom: UACT_DENOM
});

export const DEFAULT_HTTP_PORT = 80;

/** Builds a fresh HTTP port published globally on port 80 with the default HTTP options. */
export const defaultHttpExpose = (overrides?: Partial<ExposeType>): ExposeType => ({
  id: nanoid(),
  port: DEFAULT_HTTP_PORT,
  as: DEFAULT_HTTP_PORT,
  proto: "http",
  global: true,
  to: [],
  accept: [],
  ipName: "",
  httpOptions: {
    maxBodySize: defaultHttpOptions.maxBodySize,
    readTimeout: defaultHttpOptions.readTimeout,
    sendTimeout: defaultHttpOptions.sendTimeout,
    nextCases: [...defaultHttpOptions.nextCases],
    nextTries: defaultHttpOptions.nextTries,
    nextTimeout: defaultHttpOptions.nextTimeout
  },
  ...overrides
});

/**
 * Builds a fresh service bound to the given placement id and seeded with the
 * standard single-port HTTP expose / compute / pricing defaults.
 */
export const defaultService = (placementId: string, overrides?: Partial<ServiceType>): ServiceType => ({
  id: nanoid(),
  title: "service-1",
  image: "",
  sshPubKey: "",
  profile: {
    cpu: 0.1,
    gpu: 1,
    gpuModels: [{ vendor: "nvidia" }],
    hasGpu: false,
    ram: 512,
    ramUnit: "Mi",
    storage: [
      {
        size: 1,
        unit: "Gi",
        isPersistent: false,
        type: "beta2"
      }
    ]
  },
  expose: [defaultHttpExpose()],
  command: { command: "", arg: "" },
  env: [],
  placementId,
  pricing: defaultPricing(),
  count: 1,
  ...overrides
});

/**
 * Builds top-level form values for a brand-new deployment: one placement
 * paired with one service that references it. Use this anywhere the form
 * is initialized from scratch.
 */
export const defaultServiceWithPlacement = (serviceOverrides?: Partial<ServiceType>): SdlBuilderFormValuesType => {
  const placement = defaultPlacement();
  return {
    placements: [placement],
    services: [defaultService(placement.id, serviceOverrides)],
    endpoints: []
  };
};

export const defaultPersistentStorage = {
  size: 10,
  unit: "Gi",
  isPersistent: true,
  type: "beta3",
  name: "data",
  mount: "/mnt/data",
  isReadOnly: false
};

export const defaultRamStorage = {
  size: 10,
  unit: "Gi",
  isPersistent: false,
  type: "ram",
  name: "shm",
  mount: "/dev/shm",
  isReadOnly: false
};

/** The defaults applied to a freshly added GPU collection (vendor preset, model left for the user to pick). */
export const defaultGpuModel = { vendor: "nvidia", name: "", memory: "", interface: "" };

export const SSH_EXPOSE = {
  port: 22,
  as: 22,
  global: true,
  to: []
};

/**
 * The managed SSH expose row a fresh Container-VM service is seeded with: container port 22 published
 * globally as 22 over tcp. The SDL is generated straight from form state, so the row must exist on the model itself.
 */
export const vmSshExpose = (): ExposeType => ({
  id: nanoid(),
  port: 22,
  as: 22,
  proto: "tcp",
  global: true,
  to: [],
  accept: [],
  ipName: ""
});

/** Overrides for a fresh Container-VM service on the configure screen: the real distro image ref, the managed SSH expose, and the single instance VMs run as. */
export const vmServiceOverrides = (): Partial<ServiceType> => ({
  image: SSH_VM_IMAGES["Ubuntu 24.04"],
  expose: [vmSshExpose()],
  count: 1
});

/** Overrides for a fresh service running the hello world example, which listens on its own port behind the usual port 80. */
export const exampleImageServiceOverrides = (): Partial<ServiceType> => ({
  image: HELLO_WORLD_IMAGE,
  expose: [defaultHttpExpose({ port: HELLO_WORLD_PORT })]
});

export const nextCases = [
  { value: "error", label: "error" },
  { value: "timeout", label: "timeout" },
  { value: "403", label: "403" },
  { value: "404", label: "404" },
  { value: "429", label: "429" },
  { value: "500", label: "500" },
  { value: "502", label: "502" },
  { value: "503", label: "503" },
  { value: "504", label: "504" },
  { value: "off", label: "off" }
];
